import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './Sidebar.css';

function Sidebar({
  isOpen,
  onToggle,
  user,
  onLogout,
  hasActiveSubscription,
  subscriptionTier,
  monthlyChecksUsed,
  monthlyCheckLimit,
  checksRemaining,
  history,
  chatSessions = [],
  chatSessionsLoading = false,
  currentCheckId,
  currentChatId,
  onNewCheck,
  onNewChat,
  onSelectCheck,
  onDeleteCheck,
  onSelectChat,
  onDeleteChat,
  onRenameChat,
  onNavigateSettings,
  subscriptionStatus,
  profileLoading
}) {
  const [userName, setUserName] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // null = show all, 'chats', or 'checks'
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch user's name from profile
  useEffect(() => {
    if (user?.id) {
      supabase
        .from('profiles')
        .select('first_name')
        .eq('id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.first_name) {
            setUserName(data.first_name);
          }
        });
    }
  }, [user?.id]);

  // Auto-switch tab based on route
  useEffect(() => {
    if (location.pathname === '/chat') {
      setActiveTab('chats');
    } else if (location.pathname === '/checker') {
      setActiveTab('checks');
    }
  }, [location.pathname]);

  // Focus rename input when it appears
  useEffect(() => {
    if (renamingChatId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingChatId]);

  // Display name: prefer first name, fall back to email username
  const displayName = userName || user?.email?.split('@')[0] || 'User';

  // Group items by date
  const groupByDate = (items) => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    // Sort by timestamp descending
    const sorted = [...items].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    sorted.forEach(item => {
      const itemDate = new Date(item.timestamp);
      const itemDay = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());

      if (itemDay.getTime() === today.getTime()) {
        groups.today.push(item);
      } else if (itemDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(item);
      } else if (itemDay > weekAgo) {
        groups.thisWeek.push(item);
      } else {
        groups.older.push(item);
      }
    });

    return groups;
  };

  const getItemTitle = (item) => {
    // Chat items
    if (item.itemType === 'chat') {
      return item.title || 'Chat session';
    }
    // Check items
    if (item.inputType === 'url') {
      try {
        const url = new URL(item.inputSource);
        return url.hostname;
      } catch {
        return item.inputSource;
      }
    }
    if (item.inputType === 'file') {
      return item.inputSource;
    }
    // Text input - use first 30 chars
    return item.inputPreview?.substring(0, 30) + '...' || 'Text check';
  };

  // Get items for current tab (null = show all combined)
  const currentItems = activeTab === 'chats'
    ? chatSessions
    : activeTab === 'checks'
      ? history.map(item => ({ ...item, itemType: 'check' }))
      : [
          ...chatSessions,
          ...history.map(item => ({ ...item, itemType: 'check' }))
        ];

  const groupedItems = groupByDate(currentItems);

  const handleStartRename = (e, item) => {
    e.stopPropagation();
    setRenamingChatId(item.id);
    setRenameValue(item.title || '');
  };

  const handleFinishRename = (chatId) => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== (chatSessions.find(c => c.id === chatId)?.title || '')) {
      onRenameChat(chatId, trimmed);
    }
    setRenamingChatId(null);
    setRenameValue('');
  };

  const handleRenameKeyDown = (e, chatId) => {
    if (e.key === 'Enter') {
      handleFinishRename(chatId);
    } else if (e.key === 'Escape') {
      setRenamingChatId(null);
      setRenameValue('');
    }
  };

  const renderHistoryGroup = (title, items) => {
    if (items.length === 0) return null;

    return (
      <div className="history-group">
        <h4 className="history-group-title">{title}</h4>
        {items.map(item => {
          const isChat = item.itemType === 'chat';
          const isActive = isChat
            ? currentChatId === item.id
            : currentCheckId === item.id;
          const isRenaming = isChat && renamingChatId === item.id;

          return (
            <div
              key={`${item.itemType}-${item.id}`}
              className={`history-item ${isActive ? 'active' : ''}`}
              onClick={() => isChat ? onSelectChat(item.id) : onSelectCheck(item.id)}
            >
              <span className="history-item-icon">
                {isChat ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                ) : item.inputType === 'url' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                  </svg>
                ) : item.inputType === 'file' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <line x1="17" y1="10" x2="3" y2="10"/>
                    <line x1="21" y1="6" x2="3" y2="6"/>
                    <line x1="21" y1="14" x2="3" y2="14"/>
                    <line x1="17" y1="18" x2="3" y2="18"/>
                  </svg>
                )}
              </span>

              {isRenaming ? (
                <input
                  ref={renameInputRef}
                  className="rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => handleRenameKeyDown(e, item.id)}
                  onBlur={() => handleFinishRename(item.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="history-item-title">{getItemTitle(item)}</span>
              )}

              <div className="history-item-actions">
                {/* Rename button - only for chats, not while renaming */}
                {isChat && !isRenaming && (
                  <button
                    className="history-item-rename"
                    onClick={(e) => handleStartRename(e, item)}
                    aria-label="Rename chat"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                )}

                {/* Delete button */}
                {!isRenaming && (
                  <button
                    className="history-item-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      isChat ? onDeleteChat(item.id) : onDeleteCheck(item.id);
                    }}
                    aria-label={isChat ? "Delete chat" : "Delete check"}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      {/* Logo / Toggle */}
      <div className="sidebar-logo">
        {isOpen ? (
          <>
            <Link to="/" className="logo-link">
              Mindmark
            </Link>
            <button className="sidebar-toggle-btn" onClick={onToggle} aria-label="Close sidebar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
            </button>
          </>
        ) : (
          <button className="sidebar-toggle-btn" onClick={onToggle} aria-label="Open sidebar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        <button
          className={`nav-item ${location.pathname === '/checker' && !currentCheckId ? 'active' : ''}`}
          onClick={onNewCheck}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <polyline points="9 11 12 14 22 4"/>
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
          </svg>
          {isOpen && 'New Check'}
        </button>
        <button
          className={`nav-item ${location.pathname === '/chat' && !currentChatId ? 'active' : ''}`}
          onClick={onNewChat}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          {isOpen && (
            <>
              New Chat
              {!hasActiveSubscription && (
                <span className="nav-badge pro">Basic</span>
              )}
            </>
          )}
        </button>
        <button
          className={`nav-item ${location.pathname === '/documents' ? 'active' : ''}`}
          onClick={() => navigate('/documents')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
          </svg>
          {isOpen && 'Documents'}
        </button>
      </nav>

      {/* History Section */}
      <div className="sidebar-history">
        {/* Recents heading + filter tabs */}
        <div className="history-header">
          {isOpen && <span className="history-heading">Recents</span>}
          <div className="history-tabs">
            <button
              className={`history-tab ${activeTab === 'chats' ? 'active' : ''}`}
              onClick={() => setActiveTab(prev => prev === 'chats' ? null : 'chats')}
            >
              Chats
            </button>
            <button
              className={`history-tab ${activeTab === 'checks' ? 'active' : ''}`}
              onClick={() => setActiveTab(prev => prev === 'checks' ? null : 'checks')}
            >
              Checks
            </button>
          </div>
        </div>

        {/* History List */}
        <div className="history-list">
          {/* Loading state */}
          {activeTab !== 'checks' && chatSessionsLoading && (
            <div className="history-loading">
              <div className="typing-indicator-small">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!chatSessionsLoading && currentItems.length === 0 ? (
            <div className="history-empty">
              <p>
                {activeTab === 'chats'
                  ? 'No chats yet'
                  : activeTab === 'checks'
                    ? 'No checks yet'
                    : 'No history yet'
                }
              </p>
            </div>
          ) : (
            <>
              {renderHistoryGroup('Today', groupedItems.today)}
              {renderHistoryGroup('Yesterday', groupedItems.yesterday)}
              {renderHistoryGroup('Previous 7 Days', groupedItems.thisWeek)}
              {renderHistoryGroup('Older', groupedItems.older)}
            </>
          )}
        </div>
      </div>

      {/* User Profile Section */}
      <div className="sidebar-divider" />
      <div className="sidebar-footer">
        <div className="sidebar-user" onClick={onNavigateSettings}>
          <div className="user-avatar">
            {profileLoading ? '·' : (userName || user?.email)?.charAt(0).toUpperCase() || 'U'}
          </div>
          {isOpen && (
            <div className="user-info">
              <span className="user-name">{profileLoading ? '...' : displayName}</span>
              <span className="user-plan">
                {profileLoading ? (
                  '...'
                ) : subscriptionTier === 'pro' ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Pro Plan
                  </>
                ) : subscriptionTier === 'basic' ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Basic {monthlyChecksUsed}/{monthlyCheckLimit}
                  </>
                ) : (
                  `${checksRemaining} of ${monthlyCheckLimit} left`
                )}
              </span>
            </div>
          )}
          {isOpen && (
            <button className="settings-btn" aria-label="Settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
